import { registerLocaleData } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import localeDe from "@angular/common/locales/de";
import { ApplicationConfig, importProvidersFrom } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import {
  TitleStrategy,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
} from "@angular/router";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { routes } from "./app-routing.module";
import { AuthenticationSecretsService } from "./infrastructure/authentications-secrets.service";
import { AuthorizationService } from "./infrastructure/authorization.service";
import { FilterService } from "./infrastructure/filter.service";
import { AcceptLanguageHeaderHttpInterceptor } from "./infrastructure/http.interceptors/accept.language.header.http.interceptor";
import { AuthHttpInterceptor } from "./infrastructure/http.interceptors/auth.http.interceptor";
import { ErrorHttpInterceptor } from "./infrastructure/http.interceptors/error.http.interceptor";
import { PendingRequestsHttpInterceptor } from "./infrastructure/http.interceptors/pendingrequest.http.interceptor";
import { MessageService } from "./infrastructure/message.service";
import { NewsStateService } from "./infrastructure/news-state.service";
import { PendingRequestService } from "./infrastructure/pendingrequest.service";
import { LocalizedPageTitleStrategy } from "./infrastructure/pipes/localized-page-title.strategy";
import { ThemeService } from "./infrastructure/theme.service";
import { WindowRefService } from "./infrastructure/windowref.service";
import { ApiModule, BASE_PATH, Configuration } from "./services";

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./", ".json");
}

registerLocaleData(localeDe);

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    MessageService,
    ThemeService,
    NewsStateService,
    PendingRequestService,
    WindowRefService,
    AuthorizationService,
    FilterService,
    {
      provide: TitleStrategy,
      useClass: LocalizedPageTitleStrategy,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PendingRequestsHttpInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AcceptLanguageHeaderHttpInterceptor,
      multi: true,
    },
    { provide: BASE_PATH, useValue: window.location.origin },
    {
      provide: Configuration,
      useFactory: (authService: AuthenticationSecretsService) =>
        new Configuration({
          accessToken: authService.getAccessToken.bind(authService),
        }),
      deps: [AuthenticationSecretsService],
      multi: false,
    },
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(
      BrowserModule,
      ApiModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        isolate: true,
      })
    ),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: "enabled",
      })
    ),
  ],
};
